import { fromJson, type JsonValue } from "@bufbuild/protobuf";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { DirectionAnalysisSchema } from "@/gen/es/upside/opportunities/v1/opportunities_pb";
import { type APIError, api } from "@/lib/api";
import type { Opportunity as BaseOpportunity } from "./use-account-opportunities";

// Define enum for milestone sort_key to prevent magic numbers
export enum MilestoneSortKey {
  NONE = 0,
  REQUESTED = 1,
  PENDING = 2,
  DRAFT = 3,
  PUBLISHED = 4,
}

// Define enum for direction sort_key
export enum DirectionSortKey {
  UNDETERMINED = 0,
  OUTBOUND = 1,
  INBOUND = 2,
}

// Define enum for analysis status
export enum AnalysisStatus {
  NONE = "NONE",
  REQUESTED = "REQUESTED",
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
  QUEUED = "QUEUED",
}

// Define enum for direction value
export enum DirectionValue {
  INBOUND = "INBOUND",
  OUTBOUND = "OUTBOUND",
  UNDETERMINED = "UNDETERMINED",
}

// Extend the base opportunity type with our additional fields
export interface Opportunity extends BaseOpportunity {
  account_name: string;
  total_amount: number | null;
  buying_group_count: number;
  record_type_name: string | null;
  // Decomposed fields for display
  direction: string | null; // The actual direction value (INBOUND, OUTBOUND, UNDETERMINED)
  direction_generation_status: string | null; // Task processing status (queued, processing, success, failed, etc)
  milestone_analysis: "published" | "pending_approval" | null; // Analysis approval status
  milestone_analysis_generation_status: string | null; // Task processing status
  // Touchpoint metrics (calculated via MetricRenderer)
  touchpoint_count?: number;
  first_touchpoint_date?: string | null;
  last_touchpoint_date?: string | null;
}

export interface OpportunitiesResponse {
  results: Opportunity[];
  total: number;
  limit: number;
  offset: number;
  client_warning?: string; // Warning from server (e.g., "SORT_ON_METRIC_TOO_MANY_MATCHING")
}

export interface OpportunityParams {
  offset?: string;
  search?: string;
  sort_by?: string;
  sort_direction?: string;
  null_sort?: string;
  [key: string]: string | undefined;
}

export function useInfiniteOpportunities(params?: OpportunityParams) {
  return useInfiniteQuery({
    queryKey: ["opportunities", params],
    initialPageParam: 0,
    enabled: !!params,
    queryFn: async ({ pageParam }) =>
      api.get<OpportunitiesResponse>("datahub/opportunities/", {
        params: {
          ...params,
          offset: String(pageParam),
        },
      }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.results.length < lastPage.limit) {
        return undefined;
      }
      return pages.length * lastPage.limit;
    },
  });
}

export function useOpportunitySearch({
  search,
  enabled = true,
}: {
  search?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["opportunities/search", search],
    queryFn: () =>
      api.get<OpportunitiesResponse>("datahub/opportunities/search/", {
        params: { search: search || "" },
      }),
    enabled: enabled && !!search,
  });
}

export function useOpportunitiesByIds(opportunityIds?: string[]) {
  const ids = opportunityIds?.join(",") || "";
  return useQuery({
    queryKey: ["opportunities/by-ids", ids],
    queryFn: () =>
      api.get<OpportunitiesResponse>("datahub/opportunities/by-ids/", {
        params: { opportunity_ids: ids },
      }),
    enabled: !!opportunityIds?.length,
  });
}

/**
 * Hook for fetching detailed opportunity information including contacts
 */
export function useOpportunityDetails({
  opportunityId,
  accountId,
  enabled = true,
}: {
  opportunityId?: string;
  accountId?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["opportunity-details", opportunityId],
    queryFn: () => {
      if (!accountId || !opportunityId) throw new Error("Missing required IDs");
      return api
        .get<Opportunity[]>(`datahub/accounts/${accountId}/opportunities/`, {
          params: { opportunity_ids: opportunityId },
        })
        .then((data) => data[0]);
    },
    enabled: enabled && !!opportunityId && !!accountId,
  });
}

/**
 * Hook for fetching direction analysis data for an opportunity
 */
export function useOpportunityDirectionAnalysis({
  opportunityId,
  enabled = true,
}: {
  opportunityId?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["opportunity-direction-analysis", opportunityId],
    queryFn: () => {
      if (!opportunityId) throw new Error("Missing opportunity ID");
      return api
        .get<{ direction_analysis: Record<string, unknown> }>(
          `datahub/opportunities/${opportunityId}/direction-analysis/`,
        )
        .then((data) => {
          const analysis = data.direction_analysis;
          if (!analysis) return null;

          return fromJson(DirectionAnalysisSchema, {
            opportunityId: analysis.opportunity_id,
            value: analysis.value,
            opinion: analysis.opinion,
            touchpointCitations: analysis.touchpoint_citations,
            otherCitations: analysis.other_citations,
            taskId: analysis.task_id,
          } as JsonValue);
        });
    },
    enabled: enabled && !!opportunityId,
  });
}

interface RegenerateDirectionParams {
  taskId: string;
  opportunityId: string;
}

interface RegenerateDirectionResponse {
  task_id: string;
  status: string;
}

/**
 * Hook for regenerating direction analysis for an opportunity.
 * This handles both failed analyses and completed analyses with "undetermined" results.
 * Requires the taskId from the direction analysis endpoint response.
 */
export function useRegenerateDirectionAnalysis() {
  const queryClient = useQueryClient();

  return useMutation<
    RegenerateDirectionResponse,
    Error,
    RegenerateDirectionParams
  >({
    mutationFn: async ({ taskId }) => {
      return api.post<RegenerateDirectionResponse>(
        `datahub/tagging-analyses/tasks/${taskId}/retry/`,
      );
    },
    onSuccess: (_, { opportunityId }) => {
      toast.success("Direction analysis regeneration started");

      // Invalidate the direction analysis for this opportunity
      queryClient.invalidateQueries({
        queryKey: ["opportunity-direction-analysis", opportunityId],
      });

      // Invalidate the opportunities list to update status
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
      });
    },
    onError: () => {
      toast.error("Failed to regenerate direction analysis");
    },
  });
}

/**
 * Hook for fetching all opportunities matching the given filters for CSV export
 * This leverages the same API endpoint and parameter structure as useInfiniteOpportunities
 * Returns a function that can be called to trigger the export, rather than fetching immediately
 */
export function useOpportunitiesExport() {
  const mutation = useMutation<
    Opportunity[],
    Error,
    OpportunityParams | undefined,
    unknown
  >({
    mutationFn: async (params = {}) => {
      const allOpportunities: Opportunity[] = [];
      let offset = 0;
      let hasMoreData = true;

      // Paginate through all opportunities
      while (hasMoreData) {
        // Convert all params to strings and remove undefined values
        const queryParams: Record<string, string> = {
          ...params,
          limit: "50000", // Override the limit for export
          offset: offset.toString(),
        };

        // Fetch the current page of opportunities
        const response = await api.get<OpportunitiesResponse>(
          "datahub/opportunities/",
          { params: queryParams },
        );

        // Add the results to our array
        allOpportunities.push(...response.results);

        // Check if we should continue fetching
        hasMoreData = allOpportunities.length < response.total;
        // Increment the offset for the next page
        offset += response.results.length;
      }

      return allOpportunities;
    },
  });

  // Return the mutation object and a convenience trigger function
  return {
    ...mutation,
    exportOpportunities: (params?: OpportunityParams) =>
      mutation.mutate(params),
    exportOpportunitiesAsync: (params?: OpportunityParams) =>
      mutation.mutateAsync(params),
  };
}

export interface OpportunityFilterOptions {
  stage_name?: string[];
  type?: string[];
  record_type_name?: string[];
}

export function useOpportunityFilterOptions() {
  return useQuery({
    queryKey: ["datahub/opportunities/filter-options"],
    queryFn: () =>
      api.get<OpportunityFilterOptions>(
        "datahub/opportunities/filter-options/",
      ),
  });
}

/**
 * Mutation for creating bulk analysis tasks for opportunities
 */
export interface BulkAnalysisRequest {
  analysis_type: "opportunity_activation" | "opportunity_direction";
  filters: Record<string, unknown>;
}

export interface BulkAnalysisResponse {
  success_count: number;
  failed_count: number;
  errors?: string[];
}

export function useCreateBulkAnalysis() {
  return useMutation<BulkAnalysisResponse, APIError, BulkAnalysisRequest>({
    mutationFn: (data) =>
      api.post<BulkAnalysisResponse>(
        "datahub/opportunities/bulk-analysis/",
        data,
      ),
  });
}
